import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Button, TextField, MenuItem,
  Tooltip, CircularProgress, InputAdornment, Pagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import SearchIcon from '@mui/icons-material/Search';
import ScienceIcon from '@mui/icons-material/Science';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, getDocs, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { registrarVentaOrden } from '../services/ventaService';
import PaymentDialog from '../components/PaymentDialog';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { db } from '../firebase';

import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;
const PAGE_SIZE = 15;

const STATUS_CONFIG = {
  solicitado:     { label: 'Solicitado', color: '#6366F1', bg: '#EDE9FE' },
  pending_upload: { label: 'Pendiente',  color: '#D97706', bg: '#FEF3C7' },
  processing:     { label: 'Procesando', color: '#2563EB', bg: '#DBEAFE' },
  pending_review: { label: 'En revisión',color: '#CA8A04', bg: '#FEF9C3' },
  approved:       { label: 'Aprobado',   color: '#16A34A', bg: '#DCFCE7' },
  reported:       { label: 'Informado',  color: '#059669', bg: '#D1FAE5' },
};

const PAYMENT_STATUS = {
  pagado:    { label: 'Pagado',    color: '#059669', bg: '#D1FAE5' },
  no_pagado: { label: 'No pagado', color: '#DC2626', bg: '#FEE2E2' },
};

const PRIORITY_CHIP = {
  urgent:  { label: 'Urgente', color: '#DC2626', bg: '#FEE2E2' },
  routine: { label: 'Rutina',  color: '#6B7280', bg: '#F3F4F6' },
};

const getExamStatus = (order, exam) => {
  const requested = order.examsRequested || [];
  const examEntry = order.examResults?.[exam?.examTypeId];
  const orderFallback = order.status === 'solicitado' ? 'solicitado' : 'pending_upload';
  return examEntry?.status || (requested.length === 1 ? order.status : orderFallback);
};

export default function LabOrders() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userData } = useAuth();
  const { filterByBranch, activeBranchId } = useBranch();
  const isGestion = (userData?.role || '').toLowerCase() === 'gestion';
  const isTM      = (userData?.role || '') === 'Tecnólogo Médico';
  const [paymentOpen, setPaymentOpen]   = useState(false);
  const [payingOrder, setPayingOrder]   = useState(null);

  const [orders, setOrders] = useState([]);
  const [patientsMap, setPatientsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [filterPriority, setFilterPriority] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    getDocs(collection(db, 'patients')).then((snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = { species: d.data().species || '', branchIds: d.data().branchIds || [] };
      });
      setPatientsMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'labOrders'), orderBy('createdAt', 'desc')),
      (snap) => {
        setOrders(filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
        setLoading(false);
      }
    );
    return unsub;
  }, [filterByBranch]);

  const isAdmin = userData?.role === 'admin';
  const userBranchIds = userData?.branchIds || [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return orders.filter((o) => {
      // Role-based base filter
      if (isGestion && o.status !== 'solicitado') return false;
      // El Tecnólogo Médico ve órdenes pagadas y con al menos un examen no informado
      if (isTM) {
        if (o.paymentStatus !== 'pagado') return false;
        const requested = o.examsRequested || [];
        const allReported = requested.length > 0 && requested.every((e) => getExamStatus(o, e) === 'reported');
        if (allReported) return false;
      }

      // Filter by patient branchIds
      if (!isAdmin && userBranchIds.length > 0 && o.patientId) {
        const patientBranchIds = patientsMap[o.patientId]?.branchIds || [];
        if (patientBranchIds.length > 0 && !patientBranchIds.some(id => userBranchIds.includes(id))) return false;
      }

      if (filterStatus && o.status !== filterStatus) return false;
      if (filterPriority && o.priority !== filterPriority) return false;

      if (dateFrom || dateTo) {
        const raw = o.createdAt?.toDate ? o.createdAt.toDate() : (o.createdAt ? new Date(o.createdAt) : null);
        if (!raw) return false;
        const ds = `${raw.getFullYear()}-${String(raw.getMonth() + 1).padStart(2, '0')}-${String(raw.getDate()).padStart(2, '0')}`;
        if (dateFrom && ds < dateFrom) return false;
        if (dateTo && ds > dateTo) return false;
      }

      if (q && !(
        (o.patientName || '').toLowerCase().includes(q) ||
        (o.orderNumber || '').toLowerCase().includes(q) ||
        (o.vetName || '').toLowerCase().includes(q) ||
        (o.examsRequested || []).some((e) => e.examTypeName?.toLowerCase().includes(q))
      )) return false;
      return true;
    });
  }, [orders, search, filterStatus, filterPriority, dateFrom, dateTo, isGestion, isTM, patientsMap, userBranchIds, isAdmin]);

  // Expand: one row per exam per order
  const expandedRows = useMemo(() => {
    const rows = [];
    filtered.forEach((o, orderIdx) => {
      const requested = o.examsRequested || [];
      const exams = requested.length > 0
        ? requested.filter((e) => !isTM || getExamStatus(o, e) !== 'reported')
        : [{ examTypeName: '—', examTypeId: '' }];
      if (exams.length === 0) return;
      exams.forEach((exam, examIdx) => {
        rows.push({
          ...o,
          _exam:        exam,
          _examIdx:     examIdx,
          _isFirst:     examIdx === 0,
          _examCount:   exams.length,
          _orderIdx:    orderIdx,
        });
      });
    });
    return rows;
  }, [filtered, isTM]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return expandedRows.slice(start, start + PAGE_SIZE);
  }, [expandedRows, page]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const clearFilters = () => {
    setSearch(''); setFilterStatus(''); setFilterPriority('');
    setDateFrom(''); setDateTo(''); setPage(1);
  };

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ScienceIcon sx={{ color: LAB_PRIMARY, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>Órdenes de Examen</Typography>
            <Typography variant="caption" color="text.secondary">
              {filtered.length} orden{filtered.length !== 1 ? 'es' : ''} · {expandedRows.length} examen{expandedRows.length !== 1 ? 'es' : ''}
            </Typography>
          </Box>
        </Box>
        {!isTM && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => navigate('/lab/orders/new')}
            sx={{ bgcolor: LAB_PRIMARY, fontWeight: 600, borderRadius: 2 }}>
            Nueva Orden
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid #E2E8F0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Buscar paciente, N° orden, médico…"
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        {!isGestion && (
          <TextField select size="small" label="Estado" value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}
            SelectProps={{ displayEmpty: true }}>
            <MenuItem value="">Todos los estados</MenuItem>
            {Object.entries(STATUS_CONFIG)
              .filter(([k]) => isTM ? true : k !== 'solicitado')
              .map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
          </TextField>
        )}
        <TextField select size="small" label="Prioridad" value={filterPriority}
          onChange={(e) => { setFilterPriority(e.target.value); setPage(1); }} sx={{ minWidth: 130 }}
          SelectProps={{ displayEmpty: true }}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="urgent">Urgente</MenuItem>
          <MenuItem value="routine">Rutina</MenuItem>
        </TextField>
        <TextField type="date" size="small" label="Desde" value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
        <TextField type="date" size="small" label="Hasta" value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
        {(search || filterStatus || filterPriority || dateFrom || dateTo) && (
          <Button size="small" onClick={clearFilters} sx={{ color: 'text.secondary' }}>Limpiar</Button>
        )}
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: LAB_PRIMARY }}>
                {['N° Orden', 'Paciente', 'Especie', 'Examen', 'Médico V.', 'Fecha', 'Prioridad', 'Estado', 'Acciones'].map((h) => (
                  <TableCell key={h} sx={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem', py: 1.2 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={32} /></TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No se encontraron órdenes.</TableCell></TableRow>
              ) : paged.map((row, idx) => {
                const rawExamStatus = getExamStatus(row, row._exam);
                const effectiveExamStatus = STATUS_CONFIG[rawExamStatus] ? rawExamStatus : 'pending_upload';
                const examEntry  = row.examResults?.[row._exam?.examTypeId];
                const examResultId = examEntry?.resultId || (row._examCount === 1 ? row.resultId : null);
                const paymentCfg  = PAYMENT_STATUS[row.paymentStatus] || null;
                const cfg    = STATUS_CONFIG[effectiveExamStatus];
                const priCfg = PRIORITY_CHIP[row.priority] || PRIORITY_CHIP.routine;
                const species = row.patientSpecies || patientsMap[row.patientId]?.species || '—';
                // Alternate background by order group, add top border to separate orders
                const orderBg = row._orderIdx % 2 === 0 ? '#fff' : '#FAFAFA';
                const topBorder = row._isFirst && idx > 0 ? '2px solid #E2E8F0' : undefined;
                return (
                  <TableRow
                    key={`${row.id}-${row._examIdx}`}
                    sx={{ bgcolor: orderBg, borderTop: topBorder, '&:hover': { bgcolor: '#EFF6FF' } }}
                  >
                    {/* N° Orden: only on first exam row */}
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, color: LAB_PRIMARY, verticalAlign: 'top' }}>
                      {row._isFirst ? (row.orderNumber || '—') : (
                        <Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>↳</Typography>
                      )}
                    </TableCell>
                    {/* Paciente + Especie: only on first exam row */}
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, verticalAlign: 'top' }}>
                      {row._isFirst ? (row.patientName || '—') : ''}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', verticalAlign: 'top' }}>
                      {row._isFirst ? species : ''}
                    </TableCell>
                    {/* Examen individual */}
                    <TableCell sx={{ fontSize: '0.78rem', maxWidth: 200 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 600, color: LAB_PRIMARY }}>
                        {row._exam?.examTypeName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', verticalAlign: 'top' }}>
                      {row._isFirst ? (row.vetName || '—') : ''}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', verticalAlign: 'top' }}>
                      {row._isFirst ? formatDate(row.createdAt) : ''}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {row._isFirst && (
                        <Chip label={priCfg.label} size="small"
                          sx={{ bgcolor: priCfg.bg, color: priCfg.color, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'flex-start' }}>
                        <Chip label={cfg.label} size="small"
                          sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
                        {row._isFirst && paymentCfg && (
                          <Chip label={paymentCfg.label} size="small"
                            sx={{ bgcolor: paymentCfg.bg, color: paymentCfg.color, fontWeight: 600, fontSize: '0.62rem', height: 17 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {row._isFirst && (
                          <Tooltip title="Ver detalle de orden">
                            <IconButton size="small" onClick={() => navigate(`/lab/orders/${row.id}`)}
                              sx={{ color: LAB_PRIMARY }}>
                              <VisibilityIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row._isFirst && effectiveExamStatus === 'solicitado' && isGestion && (
                          <Tooltip title="Registrar pago">
                            <IconButton size="small"
                              onClick={() => { setPayingOrder(row); setPaymentOpen(true); }}
                              sx={{ color: '#6366F1' }}>
                              <AttachMoneyIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(effectiveExamStatus === 'pending_upload' || effectiveExamStatus === 'processing') && (
                          <Tooltip title="Subir resultado">
                            <IconButton size="small"
                              onClick={() => navigate(`/lab/upload?orderId=${row.id}&examTypeId=${row._exam?.examTypeId || ''}`)}
                              sx={{ color: LAB_TEAL }}>
                              <UploadFileIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {effectiveExamStatus === 'pending_review' && (
                          <Tooltip title="Generar informe">
                            <IconButton size="small" onClick={() => navigate(`/lab/report/${row.id}`)}
                              sx={{ color: '#D97706' }}>
                              <ArticleIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(effectiveExamStatus === 'approved' || effectiveExamStatus === 'reported') && (
                          <Tooltip title="Ver / regenerar informe">
                            <IconButton size="small" onClick={() => navigate(`/lab/report/${row.id}`)}
                              sx={{ color: '#16A34A' }}>
                              <ArticleIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {expandedRows.length > PAGE_SIZE && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={Math.ceil(expandedRows.length / PAGE_SIZE)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Paper>

      <PaymentDialog
        open={paymentOpen}
        onClose={() => { setPaymentOpen(false); setPayingOrder(null); }}
        onPay={async ({ paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes }) => {
          const venta = await registrarVentaOrden({
            presupuestoId: payingOrder.presupuestoId || null,
            orderId: payingOrder.id,
            orderCollection: 'labOrders',
            orderType: 'labOrder',
            orderNumber: payingOrder.orderNumber,
            patientId: payingOrder.patientId || '',
            patientName: payingOrder.patientName || '',
            patientSpecies: payingOrder.patientSpecies || '',
            ownerId: payingOrder.ownerId || '',
            ownerName: payingOrder.ownerName || '',
            fallbackItems: (payingOrder.examsRequested || []).map((e) => ({
              nombre: e.examTypeName, cantidad: 1,
              precioUnitario: e.precioArancel || Math.round((e.precioNeto || 0) * 1.19),
              tipoPrestacion: 'Laboratorio', unidadNegocio: 'Laboratorio',
            })),
            pago: { paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes },
            branchId: payingOrder.branchId || activeBranchId || userData?.branchId || '',
            user: { uid: currentUser?.uid, name: userData?.name, email: currentUser?.email },
            orderExtraUpdate: { status: 'pending_upload' },
          });
          await addDoc(collection(db, 'labAuditLog'), {
            orderId: payingOrder.id,
            action: 'payment_registered',
            performedBy: currentUser?.uid || '',
            performedAt: serverTimestamp(),
            details: `Pago registrado. Total: $${venta.total}. Método: ${paymentMethod}. Boleta: ${boletaNumber || 'N/A'}`,
          });
          setPaymentOpen(false);
          setPayingOrder(null);
        }}
        exams={payingOrder?.examsRequested || []}
        orderNumber={payingOrder?.orderNumber || ''}
      />
    </Box>
  );
}
